import { useCallback, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import useFormatPrice from "../../../hooks/useFormatPrice";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { FILTERS_STOPS_MAX } from "../../../constants";
import { UIFlightTimesAgg } from "@flights/types";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useEventTracking from "../../../hooks/useEventTracking";
import { SBSortingOrder } from "@bookingcom/flights-searchbox";
import { useSelector } from "react-redux";
import { getSearchSegments, getSearchType } from "../../../store/search/selectors";
import {
  useGetFiltersDraft,
  useGetAvailableFilters,
  useSetFiltersDraft,
  useResetFiltersDraft
} from "../SearchResults/components/boundsFilterHooks";
import { FlexibilityFilterOption } from "../../../constants/filtersMeta";
import useFlightsWebAaFiltersEmptySearch from "../../../utils/experiments/funnel/flights_web_aa_filters_empty_search";

/**
 *
 * ============================================================================
 * Any changes made to filters should be reflected in "search" and "searchResults" stores selectors.
 * ============================================================================
 */

const useSearchFiltersController = () => {
  const i18n = useI18n();
  const filtersState = useGetFiltersDraft();
  const availableFilters = useGetAvailableFilters();
  const type = useSelector(getSearchType);
  const searchSegments = useSelector(getSearchSegments);
  const { formatPrice } = useFormatPrice();
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("search_results", requestId);

  const flights_web_aa_filters_empty_search = useFlightsWebAaFiltersEmptySearch(); // eslint-disable-line @typescript-eslint/naming-convention

  const trackFiltersEvent = useCallback(
    (filters: any) => {
      const selectedFiltersTrack = {
        airlines: !!filters?.airlines?.length,
        duration: filters?.duration !== undefined,
        stops: filters?.stops !== undefined || filters?.multiSelectStops !== undefined,
        maxBudget: filters?.maxBudget !== undefined,
        maxBudgetPerAdult: filters?.maxBudgetPerAdult !== undefined,
        maxLayoverDuration: filters?.maxLayoverDuration !== undefined,
        departureIntervals: !!filters?.departureIntervals?.flat()?.length,
        arrivalIntervals: !!filters?.arrivalIntervals?.flat()?.length,
        flightTimes: !!filters?.flightTimes?.length,
        shortLayoverConnection: !!filters?.shortLayoverConnection,
        includedBaggage: !!filters?.includedBaggage?.length,
        airports: !!filters?.airports?.length,
        preferSameAirport: !!filters?.preferSameAirport,
        allDeals: !!filters?.allDeals
      };
      trackV2("select_filters", selectedFiltersTrack);
    },
    [trackV2]
  );

  const setFiltersDraft = useSetFiltersDraft();

  const setFilters = useCallback(
    (filters: Partial<typeof filtersState>) => {
      const result = { ...filtersState, ...filters };
      flights_web_aa_filters_empty_search.trackStages();
      trackFiltersEvent(result);
      setFiltersDraft(result);
    },
    [filtersState, trackFiltersEvent, setFiltersDraft, flights_web_aa_filters_empty_search]
  );

  // sort
  // ===================================================================== start
  const history = useHistory();
  const location = useLocation();
  const commitSort = useCallback(
    (s: SBSortingOrder = "BEST") => {
      const params = new URLSearchParams(location.search);
      params.set("sort", s);
      params.delete("page");
      history.replace({
        pathname: location.pathname,
        search: params.toString()
      });
    },
    [history, location.pathname, location.search]
  );
  const sortValue = (new URLSearchParams(location.search).get("sort") || "BEST") as SBSortingOrder;
  const sortOptions = [
    {
      value: "BEST" as SBSortingOrder,
      label: i18n.trans(t("flights_search_sort_best")),
      description: i18n.trans(t("flights_search_sort_best_tooltip"))
    },
    { value: "CHEAPEST" as SBSortingOrder, label: i18n.trans(t("flights_search_sort_cheapest")) },
    { value: "FASTEST" as SBSortingOrder, label: i18n.trans(t("flights_search_sort_fastest")) }
  ];
  const sortTouched = sortValue !== "BEST";
  const sort = {
    set: commitSort,
    reset: commitSort,
    value: sortValue,
    options: sortOptions,
    touched: sortTouched
  };
  // ======================================================================= end

  // duration filter
  // ===================================================================== start
  const durationSet = useCallback((duration: number) => setFilters({ duration }), [setFilters]);
  const durationReset = useCallback(() => setFilters({ duration: undefined }), [setFilters]);
  const durationOptions = {
    min: availableFilters.durationMin || 0,
    max: availableFilters.durationMax || 0
  };
  const durationValue = filtersState.duration || availableFilters.durationMax || 0;
  const durationTouched = durationValue > 0 && durationValue !== durationOptions.max;
  const duration = {
    options: durationOptions,
    value: durationValue,
    touched: durationTouched,
    set: durationSet,
    reset: durationReset
  };
  // ======================================================================= end

  // stops filter
  // ===================================================================== start
  const stopsSet = useCallback(
    (stops: number) => setFilters({ stops: stops === -1 ? undefined : stops }),
    [setFilters]
  );
  const stopsReset = useCallback(() => setFilters({ stops: undefined }), [setFilters]);
  const stopsOptions =
    availableFilters.stops
      ?.map((stopOtion) => ({
        count: stopOtion.count,
        label:
          stopOtion.numberOfStops === 0
            ? i18n.trans(t("flights_filters_stops_direct"))
            : i18n.trans(t("flights_filters_stops_one_max")),
        value: stopOtion.numberOfStops,
        price: stopOtion.minPrice ? formatPrice(stopOtion.minPrice) : "",
        roundPrice: stopOtion.minPriceRound ? formatPrice(stopOtion.minPriceRound, { decimalPlaces: 0 }) : ""
      }))
      .filter((option) => option.value <= FILTERS_STOPS_MAX) || [];

  const anyOptionPrice =
    availableFilters.stops?.[availableFilters.stops?.length - 1]?.minPrice || availableFilters.minPrice;
  const anyOptionRoundPrice =
    availableFilters.stops?.[availableFilters.stops?.length - 1]?.minPriceRound || availableFilters.minRoundPrice;

  stopsOptions.unshift({
    value: -1,
    label: i18n.trans(t("flights_filters_stops_any")),
    count: availableFilters.totalCount || 0,
    price: anyOptionPrice ? formatPrice(anyOptionPrice) : "",
    roundPrice: anyOptionRoundPrice ? formatPrice(anyOptionRoundPrice, { decimalPlaces: 0 }) : ""
  });
  const stopsValue = filtersState.stops === undefined ? -1 : filtersState.stops; // any stop, simillat as undefined
  const stopsTouched = stopsValue !== -1 && stopsValue !== undefined;
  const stops = {
    options: stopsOptions,
    value: stopsValue,
    touched: stopsTouched,
    set: stopsSet,
    reset: stopsReset
  };
  // ======================================================================= end

  // deals filter
  // ===================================================================== start
  const allDealsSet = useCallback(
    (dealsChecked: boolean) => {
      setFilters({ allDeals: dealsChecked });
    },
    [setFilters]
  );
  const allDealsReset = useCallback(() => setFilters({ allDeals: false }), [setFilters]);
  const allDealsOption = availableFilters.deals ?? undefined;
  const allDealsValue = !!filtersState.allDeals;
  const allDealsTouched = allDealsValue;
  const allDeals = {
    option: allDealsOption,
    value: allDealsValue,
    set: allDealsSet,
    reset: allDealsReset,
    touched: allDealsTouched
  };
  // ======================================================================= end

  // airlines filter
  // ===================================================================== start
  const airlinesSet = useCallback(
    (airlines: string[]) => {
      const isAll = airlines.length === availableFilters.airlines?.length;
      setFilters({ airlines: isAll ? undefined : airlines });
    },
    [setFilters, availableFilters.airlines]
  );
  const airlinesReset = useCallback(() => setFilters({ airlines: undefined }), [setFilters]);
  const airlinesOptions =
    availableFilters.airlines?.map((airline) => ({
      label: airline.name,
      count: airline.count,
      value: airline.iataCode,
      price: airline.minPrice ? formatPrice(airline.minPrice) : "",
      roundPrice: airline.minPrice ? formatPrice(airline.minPrice, { decimalPlaces: 0 }) : ""
    })) || [];
  const airlinesValue =
    filtersState.airlines === undefined ? airlinesOptions.map((a) => a.value) : filtersState.airlines;
  const airlinesTouched =
    filtersState.airlines !== undefined && filtersState.airlines.length !== airlinesOptions.length;
  const airlines = {
    options: airlinesOptions,
    value: airlinesValue,
    set: airlinesSet,
    reset: airlinesReset,
    touched: airlinesTouched
  };
  // ======================================================================= end

  // flights times filter
  // ===================================================================== start
  const flightTimesSet = useCallback((flightTimes: UIFlightTimesAgg) => setFilters({ flightTimes }), [setFilters]);
  const flightTimesReset = useCallback(() => setFilters({ flightTimes: undefined }), [setFilters]);
  const getFlightTimesTitle = useCallback(
    (idx: number) => {
      const segment = searchSegments[idx];
      return type === "MULTISTOP"
        ? `${segment.from[0].code} - ${segment.to[0].code}`
        : idx === 0
        ? i18n.trans(t("flights_filters_time_tab_departing"))
        : i18n.trans(t("flights_filters_time_tab_returning"));
    },
    [searchSegments, type, i18n]
  );
  const getFlightTimesLabel = useCallback(
    (idx: number, name: string) => {
      const isDeparture = name === "departure";
      const isMultiStop = type === "MULTISTOP";
      const segmentIdx = isMultiStop ? idx : 0;
      const fromNames = searchSegments?.[segmentIdx]?.["from"]?.map((loc) => loc.name) || [];
      const toNames = searchSegments?.[segmentIdx]?.["to"]?.map((loc) => loc.name) || [];
      let locationsNames = isDeparture ? fromNames : toNames;
      if (!isMultiStop && idx > 0) locationsNames = isDeparture ? toNames : fromNames; // return is reverse of depart
      return isDeparture
        ? i18n.trans(t("flights_filter_departs_time", { variables: { city_name: locationsNames.join(", ") } }))
        : i18n.trans(t("flights_filter_arrives_time", { variables: { city_name: locationsNames.join(", ") } }));
    },
    [searchSegments, type, i18n]
  );
  const flightTimesOptions = useMemo(
    () =>
      availableFilters.flightTimes?.map((times) => ({
        departure: times.departure.map((time) => ({ ...time, selected: false })) || [],
        arrival: times.arrival.map((time) => ({ ...time, selected: false })) || []
      })) || [],
    [availableFilters]
  );
  const flightTimesValue = useMemo(
    () => (filtersState.flightTimes?.length > 0 ? [...filtersState.flightTimes] : [...flightTimesOptions]),
    [filtersState.flightTimes, flightTimesOptions]
  );

  const flightTimesCount = useMemo(
    () =>
      flightTimesValue.map((segment) => [...segment.arrival, ...segment.departure].filter((t) => t.selected)).flat(2)
        .length || 0,
    [flightTimesValue]
  );
  const flightTimesTouched = useMemo(() => flightTimesCount > 0, [flightTimesCount]);

  const flightTimes = useMemo(
    () => ({
      options: flightTimesOptions,
      value: flightTimesValue,
      touched: flightTimesTouched,
      count: flightTimesCount,
      set: flightTimesSet,
      reset: flightTimesReset,
      getLabel: getFlightTimesLabel,
      getTitle: getFlightTimesTitle
    }),
    [
      flightTimesOptions,
      flightTimesValue,
      flightTimesTouched,
      flightTimesCount,
      flightTimesSet,
      flightTimesReset,
      getFlightTimesLabel,
      getFlightTimesTitle
    ]
  );
  // ======================================================================= end

  // layover duration filter
  // ===================================================================== start
  const setLayoverDuration = useCallback(
    (layoverDuration: number) => setFilters({ maxLayoverDuration: layoverDuration }),
    [setFilters]
  );
  const resetLayoverDuration = useCallback(() => setFilters({ maxLayoverDuration: undefined }), [setFilters]);
  const layoverDurationOptions = {
    min: availableFilters.duration?.[1]?.min || 0,
    max: availableFilters.duration?.[1]?.max || 0
  };
  const layoverDurationValue = filtersState.maxLayoverDuration || availableFilters.duration?.[1]?.max || 0;
  const layoverDurationTouched = layoverDurationValue > 0 && layoverDurationValue !== layoverDurationOptions.max;
  const layoverDuration = {
    options: layoverDurationOptions,
    value: layoverDurationValue,
    touched: layoverDurationTouched,
    set: setLayoverDuration,
    reset: resetLayoverDuration
  };

  // ======================================================================= end

  // budget per adult filter
  // this is an experiment flights_web_filters_price_per_adult
  // ===================================================================== start
  const budgetPerAdultSet = useCallback(
    (value: number) => {
      const isMax = value === availableFilters.budgetPerAdult?.max?.units;
      setFilters({ maxBudgetPerAdult: isMax ? undefined : value });
    },
    [setFilters, availableFilters.budgetPerAdult]
  );
  const budgetPerAdultReset = useCallback(() => {
    setFilters({ maxBudgetPerAdult: undefined });
  }, [setFilters]);
  const budgetPerAdultOptions = {
    min: availableFilters.budgetPerAdult?.min || { nanos: 0, units: 0, currencyCode: "" },
    max: availableFilters.budgetPerAdult?.max || { nanos: 0, units: 0, currencyCode: "" }
  };
  const budgetPerAdultValue = filtersState.maxBudgetPerAdult || budgetPerAdultOptions.max?.units;
  const budgetPerAdultTouched = budgetPerAdultValue > 0 && budgetPerAdultValue !== budgetPerAdultOptions.max?.units;
  const budgetPerAdult = {
    options: budgetPerAdultOptions,
    value: budgetPerAdultValue,
    touched: budgetPerAdultTouched,
    set: budgetPerAdultSet,
    reset: budgetPerAdultReset
  };
  // ======================================================================= end

  // airports filter
  // ===================================================================== start
  const airportsOptions = useMemo(() => {
    if (!availableFilters.airports?.byCity) return undefined;
    const searchCities = new Array<string>();
    searchSegments.forEach((segment) => {
      segment.from.forEach((loc) => searchCities.push(loc.name));
      segment.to.forEach((loc) => searchCities.push(loc.name));
    });
    const sortedAirportsData = [...availableFilters.airports.byCity].sort((a, b) => {
      const cityNameA = a.city.name;
      const cityNameB = b.city.name;
      const indexA = searchCities.indexOf(cityNameA);
      const indexB = searchCities.indexOf(cityNameB);
      return indexA - indexB;
    });
    return sortedAirportsData
      .filter((cityData) => cityData.airports.length > 1)
      .map((cityData) => ({
        city: cityData.city.name,
        airports: cityData.airports.map((airport) => ({
          name: airport.name,
          iata: airport.iataCode,
          count: airport.count || 0
        }))
      }));
  }, [availableFilters.airports?.byCity, searchSegments]);
  const airportsValue = useMemo(() => {
    if (!filtersState.airports || !airportsOptions) {
      return [];
    }
    return filtersState.airports.map((iata) => {
      for (const cityGroup of airportsOptions) {
        const airport = cityGroup.airports.find((a) => a.iata === iata);
        if (airport) {
          return {
            name: `${airport.name} (${cityGroup.city})`,
            iata,
            count: airport.count
          };
        }
      }
      return { name: iata, iata, count: 0 };
    });
  }, [filtersState.airports, airportsOptions]);
  const setAirports = useCallback(
    (airports: string[]) => {
      if (airportsOptions) {
        setFilters({ airports });
      }
    },
    [setFilters, airportsOptions]
  );
  const resetAirports = useCallback(() => setFilters({ airports: undefined }), [setFilters]);
  const airportsTouched = !!filtersState.airports;
  const airports = {
    options: airportsOptions,
    value: airportsValue,
    touched: airportsTouched,
    set: setAirports,
    reset: resetAirports
  };
  // ======================================================================= end

  // Popular Filters
  // these are some picked filters from the all available filters grouped
  // to be displayed as "popular filters" in top of all filters.
  // ===================================================================== start
  // -- popularAirlines --------------------------------------------------------
  const fakeDisabledState = useMemo(
    () => ({
      count: 0,
      set: () => {},
      reset: () => {},
      checked: false,
      disabled: true,
      names: []
    }),
    []
  );
  const popularAirlinesOptions = useMemo(() => {
    if (!availableFilters.airlines || availableFilters.airlines.length <= 5) return undefined;
    const popular = [...availableFilters.airlines].splice(0, 5);
    return {
      names: popular.map((_) => _.name),
      iata: popular.map((_) => _.iataCode),
      count: Math.max(...popular.map((_) => _.count || 0))
    };
  }, [availableFilters]);
  const isPopularAirlines = useMemo(
    () => popularAirlinesOptions && popularAirlinesOptions?.count > 0,
    [popularAirlinesOptions]
  );
  const setPopularAirlines = useCallback(
    () => setFilters({ airlines: popularAirlinesOptions?.iata }),
    [setFilters, popularAirlinesOptions]
  );
  const resetPopularAirlines = useCallback(() => setFilters({ airlines: undefined }), [setFilters]);
  const isPopularAirlinesChecked = useMemo(() => {
    return !!popularAirlinesOptions?.iata?.every((_) => filtersState.airlines?.includes(_));
  }, [filtersState, popularAirlinesOptions]);
  const popularAirlines = useMemo(() => {
    if (!isPopularAirlines) return fakeDisabledState;
    return {
      names: popularAirlinesOptions?.names,
      set: setPopularAirlines,
      reset: resetPopularAirlines,
      checked: isPopularAirlinesChecked,
      count: popularAirlinesOptions?.count,
      disabled: false
    };
  }, [
    isPopularAirlines,
    setPopularAirlines,
    resetPopularAirlines,
    isPopularAirlinesChecked,
    popularAirlinesOptions,
    fakeDisabledState
  ]);

  // -- cabinBaggage -----------------------------------------------------------
  const cabinBaggageOptions = useMemo(() => {
    return availableFilters.baggage?.find((_) => _.baggageType === "CABIN");
  }, [availableFilters]);
  const setCabinBaggage = useCallback(() => {
    const i = filtersState.includedBaggage || [];
    const includedBaggage = i.includes("CABIN") ? filtersState.includedBaggage : [...i, "CABIN"];
    setFilters({ includedBaggage });
  }, [setFilters, filtersState]);
  const resetCabinBaggage = useCallback(() => {
    setFilters({ includedBaggage: (filtersState.includedBaggage || []).filter((_) => _ !== "CABIN") });
  }, [setFilters, filtersState]);
  const isCabinBaggageChecked = useMemo(() => (filtersState.includedBaggage || []).includes("CABIN"), [filtersState]);
  const countCabinBaggage = useMemo(() => cabinBaggageOptions?.count, [cabinBaggageOptions]);
  const cabinBaggage = useMemo(() => {
    if (!cabinBaggageOptions?.count) return fakeDisabledState;
    return {
      set: setCabinBaggage,
      reset: resetCabinBaggage,
      checked: isCabinBaggageChecked,
      count: countCabinBaggage,
      disabled: false
    };
  }, [
    setCabinBaggage,
    resetCabinBaggage,
    isCabinBaggageChecked,
    countCabinBaggage,
    cabinBaggageOptions,
    fakeDisabledState
  ]);

  // -- earlyDeparture ---------------------------------------------------------
  const earlyDepartureOptions = useMemo(() => {
    const available = availableFilters?.flightTimes?.[0]?.departure?.find(
      (_) => _.start === "00:00" && _.end === "05:59"
    );
    const state = filtersState?.flightTimes?.[0]?.departure?.find((_) => _.start === "00:00" && _.end === "05:59");
    return { ...state, count: available?.count || state?.count };
  }, [availableFilters, filtersState]);
  const setEarlyDeparture = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 0) return { ...flightTime };
      return {
        ...flightTime,
        departure: flightTime.departure.map((time) => {
          if (time.start !== "00:00" || time.end !== "05:59") return { ...time };
          return { ...time, selected: true };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const resetEarlyDeparture = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 0) return { ...flightTime };
      return {
        ...flightTime,
        departure: flightTime.departure.map((time) => {
          if (time.start !== "00:00" || time.end !== "05:59") return { ...time };
          return { ...time, selected: false };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const earlyDeparture = useMemo(() => {
    if (!earlyDepartureOptions?.count) return fakeDisabledState;
    return {
      set: setEarlyDeparture,
      reset: resetEarlyDeparture,
      checked: earlyDepartureOptions.selected,
      count: earlyDepartureOptions.count,
      disabled: false
    };
  }, [setEarlyDeparture, resetEarlyDeparture, earlyDepartureOptions, fakeDisabledState]);

  // -- morningDeparture -------------------------------------------------------
  const morningDepartureOptions = useMemo(() => {
    const available = availableFilters?.flightTimes?.[0]?.departure?.find(
      (_) => _.start === "06:00" && _.end === "11:59"
    );
    const state = filtersState?.flightTimes?.[0]?.departure?.find((_) => _.start === "06:00" && _.end === "11:59");
    return { ...state, count: available?.count || state?.count };
  }, [availableFilters, filtersState]);
  const setMorningDeparture = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 0) return { ...flightTime };
      return {
        ...flightTime,
        departure: flightTime.departure.map((time) => {
          if (time.start !== "06:00" || time.end !== "11:59") return { ...time };
          return { ...time, selected: true };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const resetMorningDeparture = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 0) return { ...flightTime };
      return {
        ...flightTime,
        departure: flightTime.departure.map((time) => {
          if (time.start !== "06:00" || time.end !== "11:59") return { ...time };
          return { ...time, selected: false };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const morningDeparture = useMemo(() => {
    if (!morningDepartureOptions?.count) return fakeDisabledState;
    return {
      set: setMorningDeparture,
      reset: resetMorningDeparture,
      checked: morningDepartureOptions.selected,
      count: morningDepartureOptions.count,
      disabled: false
    };
  }, [setMorningDeparture, resetMorningDeparture, morningDepartureOptions, fakeDisabledState]);

  // -- Evening Return -------------------------------------------------------
  const eveningReturnOptions = useMemo(() => {
    const available =
      type === "ROUNDTRIP"
        ? availableFilters?.flightTimes?.[1]?.departure?.find((_) => _.start === "18:00" && _.end === "23:59")
        : undefined;
    const state =
      type === "ROUNDTRIP"
        ? filtersState?.flightTimes?.[1]?.departure?.find((_) => _.start === "18:00" && _.end === "23:59")
        : undefined;
    return { ...state, count: available?.count || state?.count };
  }, [type, availableFilters, filtersState]);
  const setEveningReturn = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 1) return { ...flightTime };
      return {
        ...flightTime,
        departure: flightTime.departure.map((time) => {
          if (time.start !== "18:00" || time.end !== "23:59") return { ...time };
          return { ...time, selected: true };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const resetEveningReturn = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 1) return { ...flightTime };
      return {
        ...flightTime,
        departure: flightTime.departure.map((time) => {
          if (time.start !== "18:00" || time.end !== "23:59") return { ...time };
          return { ...time, selected: false };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const eveningReturn = useMemo(() => {
    if (!eveningReturnOptions?.count) return fakeDisabledState;
    return {
      set: setEveningReturn,
      reset: resetEveningReturn,
      checked: eveningReturnOptions.selected,
      count: eveningReturnOptions.count,
      disabled: false
    };
  }, [setEveningReturn, resetEveningReturn, eveningReturnOptions, fakeDisabledState]);

  // -- lateArrival ------------------------------------------------------------
  const lateArrivalOptions = useMemo(() => {
    if (type === "MULTISTOP") return undefined;
    const available = availableFilters?.flightTimes?.[0]?.arrival?.find(
      (_) => _.start === "18:00" && _.end === "23:59"
    );
    const state = filtersState?.flightTimes?.[0]?.arrival?.find((_) => _.start === "18:00" && _.end === "23:59");
    return { ...state, count: available?.count || state?.count };
  }, [type, availableFilters, filtersState]);
  const setLateArrival = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 0) return { ...flightTime };
      return {
        ...flightTime,
        arrival: flightTime.arrival.map((time) => {
          if (time.start !== "18:00" || time.end !== "23:59") return { ...time };
          return { ...time, selected: true };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const resetLateArrival = useCallback(() => {
    const value = flightTimes.value.map((flightTime, idx) => {
      if (idx !== 0) return { ...flightTime };
      return {
        ...flightTime,
        arrival: flightTime.arrival.map((time) => {
          if (time.start !== "18:00" || time.end !== "23:59") return { ...time };
          return { ...time, selected: false };
        })
      };
    });
    flightTimesSet(value);
  }, [flightTimes, flightTimesSet]);
  const lateArrival = useMemo(() => {
    if (!lateArrivalOptions?.count) return fakeDisabledState;
    return {
      set: setLateArrival,
      reset: resetLateArrival,
      checked: lateArrivalOptions.selected,
      count: lateArrivalOptions.count,
      disabled: false
    };
  }, [setLateArrival, resetLateArrival, lateArrivalOptions, fakeDisabledState]);

  // -- shortLayover -----------------------------------------------------------
  const shortLayoverConnectionOptions = useMemo(() => availableFilters.shortLayoverConnection, [availableFilters]);
  const setShortLayoverConnection = useCallback(() => setFilters({ shortLayoverConnection: true }), [setFilters]);
  const resetShortLayoverConnection = useCallback(
    () => setFilters({ shortLayoverConnection: undefined }),
    [setFilters]
  );
  const isShortLayoverConnectionChecked = useMemo(() => !!filtersState.shortLayoverConnection, [filtersState]);
  const shortLayoverConnection = useMemo(() => {
    if (!shortLayoverConnectionOptions) return fakeDisabledState;
    return {
      count: shortLayoverConnectionOptions?.count,
      set: setShortLayoverConnection,
      reset: resetShortLayoverConnection,
      checked: isShortLayoverConnectionChecked,
      disabled: false
    };
  }, [
    shortLayoverConnectionOptions,
    setShortLayoverConnection,
    resetShortLayoverConnection,
    isShortLayoverConnectionChecked,
    fakeDisabledState
  ]);

  // -- flexibility -----------------------------------------------------------
  const setFlexibility = useCallback(
    (isChecked: boolean, filter: FlexibilityFilterOption) => {
      let filters = filtersState.flexibility || [];
      filters = filters.filter((name) => name !== filter);
      if (isChecked) filters.push(filter);
      setFilters({ flexibility: filters });
    },
    [setFilters, filtersState]
  );

  const flexibilityTouched = !!filtersState.flexibility?.length;

  const flexibility = {
    flexibleTicket: {
      count: availableFilters.flexibleTicket?.count,
      checked: !!filtersState.flexibility?.includes("flexibleTicket")
    },
    set: setFlexibility,
    touched: flexibilityTouched
  };

  // ======================================================================= end

  const resetFiltersDraft = useResetFiltersDraft();
  const resetAllFilters = useCallback(() => {
    resetFiltersDraft();
  }, [resetFiltersDraft]);

  const resetWeekendTopFilters = useCallback(() => {
    setFilters({
      stops: undefined,
      maxBudgetPerAdult: undefined,
      includedBaggage: (filtersState.includedBaggage || []).filter((_) => _ !== "CABIN"),
      flightTimes: flightTimes.value.map((flightTime, idx) => {
        const result = { ...flightTime };
        if (idx === 0) {
          result.departure = flightTime.departure.map((time) => {
            if (time.start !== "06:00" || time.end !== "11:59") return { ...time };
            return { ...time, selected: false };
          });
        }
        if (idx === 1) {
          result.departure = flightTime.departure.map((time) => {
            if (time.start !== "18:00" || time.end !== "23:59") return { ...time };
            return { ...time, selected: false };
          });
        }
        return result;
      })
    });
  }, [setFilters, flightTimes, filtersState]);

  const appliedFiltersCount = [
    durationTouched,
    stopsTouched,
    airlinesTouched,
    allDealsTouched,
    flightTimesTouched,
    budgetPerAdultTouched,
    airportsTouched,
    flexibilityTouched,
    layoverDurationTouched
  ].filter(Boolean).length;

  return {
    resetAllFilters,
    resetWeekendTopFilters,
    appliedFiltersCount,
    duration,
    stops,
    airlines,
    allDeals,
    flightTimes,
    sort,
    budgetPerAdult,
    airports,
    // popular filters
    popularAirlines,
    cabinBaggage,
    earlyDeparture,
    morningDeparture,
    eveningReturn,
    lateArrival,
    shortLayoverConnection,
    flexibility,
    layoverDuration
  };
};

export default useSearchFiltersController;
